import { AlertCircle, CheckCircle, XCircle } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import Confetti from "react-confetti";
import { useParams } from "react-router-dom";

const PaymentSuccess: React.FC = () => {
	const params = useParams();
	const paymentId = params.id;
	const searchParams = new URLSearchParams(window.location.search);
	const redirectStatus = searchParams.get("redirect_status");

	const [windowDimensions, setWindowDimensions] = useState({
		width: window.innerWidth,
		height: window.innerHeight,
	});
	const [showConfetti, setShowConfetti] = useState(true);

	useEffect(() => {
		const handleResize = () => {
			setWindowDimensions({
				width: window.innerWidth,
				height: window.innerHeight,
			});
		};

		window.addEventListener("resize", handleResize);

		// Stop confetti after 5 seconds
		const timer = setTimeout(() => {
			setShowConfetti(false);
		}, 5000);

		return () => {
			window.removeEventListener("resize", handleResize);
			clearTimeout(timer);
		};
	}, []);

	if (redirectStatus === "failed") {
		return (
			<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50 flex items-center justify-center">
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
					<div className="max-w-2xl mx-auto">
						<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
							<div className="text-center py-8 sm:py-12">
								<XCircle className="w-14 h-14 sm:w-16 sm:h-16 text-red-400 mx-auto mb-4 sm:mb-6" />
								<h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
									Payment Cancelled
								</h1>
								<p className="text-gray-600 mb-6 text-base sm:text-lg">
									Your payment was not completed. No charge has been made.
								</p>
								{paymentId && (
									<a
										href={`/pay/${paymentId}`}
										className="inline-block bg-gradient-to-r from-orange-400 to-pink-400 hover:from-orange-500 hover:to-pink-500 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300"
									>
										Try Again
									</a>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	if (redirectStatus === "processing") {
		return (
			<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50 flex items-center justify-center">
				<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
					<div className="max-w-2xl mx-auto">
						<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
							<div className="text-center py-8 sm:py-12">
								<AlertCircle className="w-14 h-14 sm:w-16 sm:h-16 text-yellow-500 mx-auto mb-4 sm:mb-6" />
								<h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
									Payment Processing
								</h1>
								<p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-lg">
									Your payment is being processed. We'll send you a confirmation email once it's
									complete.
								</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-pink-50 via-orange-50 to-yellow-50 flex items-center justify-center">
			{showConfetti && (
				<Confetti
					width={windowDimensions.width}
					height={windowDimensions.height}
					recycle={false}
					numberOfPieces={200}
					gravity={0.1}
				/>
			)}

			<div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
				<div className="max-w-2xl mx-auto">
					<div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 lg:p-8">
						<div className="text-center py-8 sm:py-12">
							<CheckCircle className="w-14 h-14 sm:w-16 sm:h-16 text-green-500 mx-auto mb-4 sm:mb-6" />
							<h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">
								Payment Successful!
							</h1>
							<p className="text-gray-600 mb-3 sm:mb-4 text-base sm:text-lg">
								Thank you for your payment! Your transaction has been completed successfully.
							</p>
							<p className="text-sm sm:text-base text-gray-500 mb-6">
								You should receive a confirmation email shortly.
							</p>

							<div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
								<p className="text-green-800 text-sm sm:text-base font-medium">
									🎉 Your payment has been processed and confirmed
								</p>
							</div>

							<div className="text-xs sm:text-sm text-gray-400 space-y-1">
								<p>💳 Payment processed securely</p>
								<p>📧 Receipt sent to your email</p>
								<p>🛡️ Transaction protected by encryption</p>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PaymentSuccess;
